import {
  ForgeStep, Expression, ExpressionVar, ExpressionBinaryOp,
  ExpressionUnaryOp, ExpressionFnCall,
  QueryCondition, QueryOrCondition, QueryConditionItem,
  QueryAaobFilter, QueryAaobLabelExists, QueryAndCondition,
} from './forge.types';
import { PageContext, PageContextEntity, PageContextEntityField } from './forge.types';
import { guardEntityReferences, isGeneralAobBlockTargetingAaob, AAOB_VALUE_TYPE } from './forge-entity-ref-guard';
import { orBranchParts } from './condition-fold';
import { SYSTEM_SET_VISIBLE_BLOCKS } from '../../../../shared/components/atlas/atlas.utils';
import {
  SETTER_BLOCK_FOR_TYPE, GETTER_BLOCK_FOR_TYPE,
  CONDITION_SETTER_FOR_TYPE, CONDITION_BLOCK_FOR_TYPE,
  AOB_SETTER_FOR_TYPE, AOB_GETTER_FOR_TYPE,
} from './block-catalog';

export interface LoadResult {
  success: boolean;
  warnings: string[];
  errors: string[];
}

/** Which palette this function belongs to. Decides which return block a RETURN step compiles back to. */
export type ForgeLane = 'function' | 'conditional' | 'value';

interface Ctx {
  ws: any;
  pc: PageContext;
  warnings: string[];
  errors: string[];
  /**
   * Defaults to 'function' -> return_boolean, which is today's behaviour unchanged. Sealed Forge's
   * prompt has RETURN removed (2026-08-07, Benchmark 0.1 V16), so in practice only the conditional
   * lane produces RETURN steps, and it wants return_boolean.
   */
  lane: ForgeLane;
}

// === Entry Point ===

export function loadStepsToWorkspace(
  workspace: any,
  functionName: string,
  steps: ForgeStep[],
  pageContext: PageContext,
  lane: ForgeLane = 'function'
): LoadResult {
  const result: LoadResult = { success: false, warnings: [], errors: [] };

  // Resolve dotted paths → Blockly encodedIds before loading
  const pathMap = buildPathMap(pageContext);
  const resolvedSteps = pathMap.size > 0 ? resolveStepPaths(steps, pathMap) : steps;

  const ctx: Ctx = { ws: workspace, pc: pageContext, warnings: result.warnings, errors: result.errors, lane };

  try {
    const funcBlock = workspace.newBlock('procedures_defnoreturn');
    funcBlock.setFieldValue(functionName, 'NAME');

    const firstBlock = chainSteps(resolvedSteps, ctx);
    if (firstBlock) {
      const input = funcBlock.getInput('STACK');
      if (input?.connection && firstBlock.previousConnection) {
        input.connection.connect(firstBlock.previousConnection);
      }
    }

    funcBlock.initSvg();
    funcBlock.render();
    result.success = true;
  } catch (e: any) {
    result.errors.push(`Failed to create function: ${e.message}`);
  }

  return result;
}

// === Step Chain ===

/**
 * The LAST block of an already-connected chain.
 *
 * ⚠ THE FIX FOR A REORDERING BUG THAT PRODUCED CORRECT-LOOKING, WRONG CODE.
 *
 * `compileStep` may return a chain rather than a block — `compileQuery` builds
 * `[…conditions, …orders, query]` and `chainStatementBlocks` hands back its HEAD. `chainSteps` used to
 * treat that head as the tail, so the next step was connected to a `nextConnection` that was already
 * occupied, and Blockly BUMPED the existing remainder to the end of the newly inserted chain.
 *
 * A correctly ordered plan then emitted its steps interleaved:
 *
 *     SET isLoading = true          →  isLoading = true;
 *     QUERY Lead (orderBy) → list      clearOrders();          ← query, part 1
 *     SET total = length(list)         total = …;              ← spliced INTO the query
 *     SET isLoading = false            isLoading = false;      ← spliced INTO the query
 *                                      addOrder();             ← query, part 2, bumped
 *                                      list = await query();   ← runs LAST
 *
 * The count is taken before the query fills the list and the flag is cleared before the await it
 * brackets — reached from a plan that was entirely correct. `assessPlan` cannot catch it: it reads
 * STEPS, and the steps were fine.
 *
 * ─── MEASURED BLAST RADIUS, so this is not overstated ─────────────────────────────────────────────
 * Only `QUERY` with `conditions`/`orderBy` and `AGGREGATE_QUERY` with `conditions` compile to more than
 * one block, and only when followed by a further step. Everything else returns a single block, where
 * `lastInChain(block) === block` and this changes nothing.
 *
 * · 15 of 261 real stored plans on non-prod clients would emit differently with this fix.
 * · 0 of 1,875 stored function BODIES show the splice — because every one of them predates the
 *   imperative-condition change (they compiled conditions into the query block's own CONDITIONS input,
 *   which Phase 3.5 removed). So this has NOT silently corrupted saved work; it is a forward-facing
 *   bug that every new generation with a filter or a sort would hit.
 *
 * It resembles the documented "no loading state, Page 1 of 0" report, and resemblance is all that can
 * be claimed: there is no evidence linking the two, and the code that produced that page is not in a
 * form this explains.
 *
 * NOT headless-only either way — `chainSteps` is the editor's Forge-apply path too.
 */
function lastInChain(block: any): any {
  let cur = block;
  // Guarded: a malformed chain must not spin. No real plan approaches this depth.
  for (let i = 0; i < 10000 && cur?.getNextBlock?.(); i++) cur = cur.getNextBlock();
  return cur;
}

function chainSteps(steps: ForgeStep[], ctx: Ctx): any | null {
  let first: any = null;
  let tail: any = null;
  for (const step of steps) {
    const block = compileStep(step, ctx);
    if (!block) continue;
    if (!first) { first = block; tail = lastInChain(block); }
    else if (tail?.nextConnection && block.previousConnection) {
      tail.nextConnection.connect(block.previousConnection);
      // The block just connected may itself be the HEAD of a chain, so advance to ITS end rather than
      // to the block returned. This single line is the bug.
      tail = lastInChain(block);
    }
  }
  return first;
}

// === Step Router ===

function compileStep(step: ForgeStep, ctx: Ctx): any | null {
  try {
    switch (step.action) {
      case 'SET': return compileSet(step, ctx);
      case 'IF': return compileIf(step, ctx);
      case 'LOOP_RANGE': return compileLoopRange(step, ctx);
      case 'LOOP_FOREACH': return compileLoopForEach(step, ctx);
      case 'QUERY': return compileQuery(step, ctx);
      case 'AGGREGATE_QUERY': return compileAggregateQuery(step, ctx);
      case 'CREATE_ENTITY': return compileCreateEntity(step, ctx);
      case 'UPDATE_ENTITY': return compileUpdateEntity(step, ctx);
      case 'SET_ENTITY_FROM_LIST': return compileSetEntityFromList(step, ctx);
      case 'SET_AOB_REF': return compileSetAobRef(step, ctx);
      case 'SET_AOB_FROM_AOB': return compileSetAobFromAob(step, ctx);
      case 'PUSH_TO_ARRAY': return compilePushToArray(step, ctx);
      case 'PUSH_TO_ENTITY_LIST': return compilePushToEntityList(step, ctx);
      case 'DELETE_FROM_LIST': return compileDeleteFromList(step, ctx);
      case 'CALL_FUNCTION': return compileCallFunction(step, ctx);
      case 'CALL_WORKFLOW': return compileCallWorkflow(step, ctx);
      case 'TIME_DIFF': return compileTimeDiff(step, ctx);
      case 'CHECK_LOCATION_ACCESS': return compileCheckLocationAccess(step, ctx);
      case 'ASK_LOCATION_ACCESS': return ir(mk(ctx, 'ask_location_access'));
      case 'ASYNC': return compileAsync(step, ctx);
      case 'RESOLVE_ASYNC': return compileResolveAsync(step, ctx);
      case 'TOAST': return compileToast(step, ctx);
      case 'NAVIGATE': return compileNavigate(step, ctx);
      case 'OPEN_EXTERNAL': return compileOpenExternal(step, ctx);
      case 'LOADING': return compileLoading(step, ctx);
      case 'RETURN': return compileReturn(step, ctx);
      case 'BREAK': return ir(mk(ctx, 'controls_flow_statements', { FLOW: 'BREAK' }));
      case 'CONTINUE': return ir(mk(ctx, 'controls_flow_statements', { FLOW: 'CONTINUE' }));
      case 'GET_CURRENT_USER': return compileGetCurrentUser(step, ctx);
      case 'GET_USERS_LIST': return compileGetUsersList(step, ctx);
      case 'GET_ROLES_LIST': return compileGetRolesList(step, ctx);
      case 'CREATE_USER': return ir(mk(ctx, 'create_user'));
      case 'EDIT_USER': return compileEditUser(step, ctx);
      case 'LOGOUT': return ir(mk(ctx, 'logout_user'));
      case 'TEXT_APPEND': return compileTextAppend(step, ctx);
      case 'REPEAT': return compileRepeat(step, ctx);
      case 'FOR_LOOP' as any: return compileLoopRange(step, ctx);
      case 'WHILE_UNTIL': return compileWhileUntil(step, ctx);
      case 'SET_AOB_FIELD': return compileSetAobField(step, ctx);
      case 'SET_AAOB_KEY_VALUE': return compileSetAaobKeyValue(step, ctx);
      case 'AAOB_AGGREGATE_QUERY': return compileAaobAggregateQuery(step, ctx);
      default:
        ctx.warnings.push(`Unknown action: ${(step as any).action}`);
        return null;
    }
  } catch (e: any) {
    ctx.errors.push(`Failed to compile ${step.action}: ${e.message}`);
    return null;
  }
}

// === Step Compilers ===

function compileSet(step: { target: string; value: Expression }, ctx: Ctx): any {
  const varType = resolveVarType(step.target, ctx);
  const b = mk(ctx, SETTER_BLOCK_FOR_TYPE[varType] || 'set_variable');
  setVar(b, 'VAR', step.target, ctx);
  cv(b, 'VALUE', expr(step.value, ctx));
  return ir(b);
}

function compileIf(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'controls_if');
  const eic = step.branches.length - 1;
  const hasElse = !!step.else?.length;
  if (eic > 0 || hasElse) {
    b.elseifCount_ = eic;
    b.elseCount_ = hasElse ? 1 : 0;
    b.updateShape_();
  }
  for (let i = 0; i < step.branches.length; i++) {
    cv(b, `IF${i}`, expr(step.branches[i].condition, ctx));
    cs(b, `DO${i}`, chainSteps(step.branches[i].then, ctx));
  }
  if (hasElse) cs(b, 'ELSE', chainSteps(step.else, ctx));
  return ir(b);
}

function compileLoopRange(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'custom_loop');
  cv(b, 'FROM', expr(step.from, ctx));
  cv(b, 'TO', expr(step.to, ctx));
  cv(b, 'STEP', expr(step.step ?? 1, ctx));
  const vb = mk(ctx, 'get_variable');
  setVar(vb, 'VAR', step.indexVar, ctx);
  cv(b, 'VARIABLE', ir(vb));
  cs(b, 'DO', chainSteps(step.body, ctx));
  return ir(b);
}

function compileLoopForEach(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'controls_forEach');
  setVar(b, 'VAR', step.itemVar, ctx);
  cv(b, 'LIST', expr(step.list, ctx));
  cs(b, 'DO', chainSteps(step.body, ctx));
  return ir(b);
}

function compileQuery(step: any, ctx: Ctx): any {
  const entity = findEntity(step.entity, ctx);
  const entityId = entity?.id || step.entity;
  const entityName = entity?.name || step.entity;

  // Phase 3.5: get_query only retains C (limit) + D (offset) sockets.
  // B (ORDER BY), CONDITIONS, EC (child entities) were removed — use imperative blocks.
  const q = mk(ctx, 'get_query');
  sf(q, 'VAR', entityId);
  if (step.limit !== undefined) cv(q, 'C', expr(step.limit, ctx));
  if (step.offset !== undefined) cv(q, 'D', expr(step.offset, ctx));
  ir(q);

  const b = mk(ctx, 'set_entity_list_variable');
  setVar(b, 'VAR', step.destination, ctx);
  cv(b, 'VALUE', q);
  ir(b);

  const condBlocks = (step.conditions?.length)
    ? emitImperativeConditionBlocks(step.conditions, entityName, ctx) : [];
  const orderBlocks = emitImperativeOrderBlocks(step, entityName, ctx);
  const all = [...condBlocks, ...orderBlocks, b];
  return chainStatementBlocks(all);
}

function compileAggregateQuery(step: any, ctx: Ctx): any {
  const entity = findEntity(step.entity, ctx);
  const entityId = entity?.id || step.entity;
  const entityName = entity?.name || step.entity;

  const a = mk(ctx, 'entity_aggregate_query');
  sf(a, 'VAR', entityId);
  sf(a, 'OP', step.op);
  if (step.column) {
    // The COLUMN FieldVariable accepts ONLY type 'NUMBER' (the sentinel type from NAN_SENTINELS).
    // Entity fields are registered with type 'Number' (Pascal case), which the FieldVariable rejects
    // — it falls back to NANENTITYNUMBER and the code generator emits `null`.
    //
    // Fix: declare a dedicated __AGG_NUM-suffixed alias with type 'NUMBER'. The code generator
    // already strips '__AGG_NUM' (`columnRaw.replace('__AGG_NUM', '')`) to recover the real column
    // name, so this suffix was designed for exactly this purpose.
    const field = resolveEntityField(entity, step.column);
    const baseId = field?.encodedId || (entityId + '$$' + step.column);
    const aggId = baseId + '__AGG_NUM';
    if (!ctx.ws.getVariableById(aggId)) {
      ctx.ws.createVariable(aggId, 'NUMBER', aggId);
    }
    sf(a, 'COLUMN', aggId);
  } else if (step.op === 'count') {
    // count needs no column — remove the COLUMN_INPUT row so the block shows
    // "count Entity where …" instead of "count Entity . NANENTITYNUMBER where …"
    a.removeInput?.('COLUMN_INPUT');
  }
  // Phase 3.5: CONDITIONS socket removed — use imperative blocks.
  ir(a);

  const b = mk(ctx, 'set_variable');
  setVar(b, 'VAR', step.destination, ctx);
  cv(b, 'VALUE', a);
  ir(b);

  const condBlocks = (step.conditions?.length)
    ? emitImperativeConditionBlocks(step.conditions, entityName, ctx) : [];
  if (condBlocks.length === 0) return b;
  return chainStatementBlocks([...condBlocks, b]);
}

function compileCreateEntity(step: any, ctx: Ctx): any {
  const entity = findEntity(step.entity, ctx);
  const entityId = entity?.id || step.entity;
  const b = mk(ctx, 'create_entity');
  sf(b, 'VAR', entityId);
  if (step.destination) {
    const g = mk(ctx, 'get_entity_variable');
    setVar(g, 'VAR', step.destination, ctx);
    cv(b, 'ENTITY', ir(g));
  }
  if (step.entityReferences?.length) {
    // A3/#13: drop wrong-entity references (linked entity type ≠ field's referenceEntityName) before emitting.
    const refs = guardEntityReferences(step.entityReferences, ctx.pc, ctx.warnings);
    if (refs.length) cv(b, 'VALUE', buildEntRefChain(refs, ctx));
  }
  if (step.successFlag) {
    const fg = mk(ctx, 'get_variable');
    setVar(fg, 'VAR', step.successFlag, ctx);
    cv(b, 'FLAG', ir(fg));
  }
  return ir(b);
}

function compileUpdateEntity(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'update_entity');
  const g = mk(ctx, 'get_entity_variable');
  setVar(g, 'VAR', step.entityVar, ctx);
  cv(b, 'VALUE', ir(g));
  if (step.entityReferences?.length) {
    // A3/#13: drop wrong-entity references before emitting (see guardEntityReferences).
    const refs = guardEntityReferences(step.entityReferences, ctx.pc, ctx.warnings);
    if (refs.length) cv(b, 'ENTITY_REFERENCE_SELECTION', buildEntRefChain(refs, ctx));
  }
  return ir(b);
}

function compileSetEntityFromList(step: any, ctx: Ctx): any {
  const inner = mk(ctx, 'set_entity_variable_by_index_of_entity_list');
  const lg = mk(ctx, 'get_entity_list_variable');
  setVar(lg, 'VAR', step.listVar, ctx);
  cv(inner, 'VALUE', ir(lg));
  if (typeof step.indexVar === 'string') {
    const ig = mk(ctx, 'get_variable');
    setVar(ig, 'VAR', step.indexVar, ctx);
    cv(inner, 'INDEX', ir(ig));
  } else {
    cv(inner, 'INDEX', expr(step.indexVar, ctx));
  }
  ir(inner);

  const b = mk(ctx, 'set_entity_variable');
  setVar(b, 'VAR', step.target, ctx);
  cv(b, 'VALUE', inner);
  return ir(b);
}

function compileSetAobRef(step: any, ctx: Ctx): any {
  const inner = mk(ctx, 'get_reference_entity_variable_for_AOB_reference_AOB');
  setVar(inner, 'VAR', step.listVar, ctx);
  if (typeof step.indexVar === 'string') {
    const ig = mk(ctx, 'get_variable');
    setVar(ig, 'VAR', step.indexVar, ctx);
    cv(inner, 'VALUE', ir(ig));
  } else {
    cv(inner, 'VALUE', expr(step.indexVar, ctx));
  }
  ir(inner);

  const b = mk(ctx, 'set_reference_entity_variable_for_AOB');
  setVar(b, 'VAR', step.target, ctx);
  cv(b, 'VALUE', inner);
  return ir(b);
}

function compileSetAobFromAob(step: any, ctx: Ctx): any {
  const inner = mk(ctx, 'get_reference_entity_variable_for_AOB_reference_AOB');
  setVar(inner, 'VAR', step.parentRef, ctx);
  if (typeof step.indexVar === 'string') {
    const ig = mk(ctx, 'get_variable');
    setVar(ig, 'VAR', step.indexVar, ctx);
    cv(inner, 'VALUE', ir(ig));
  } else {
    cv(inner, 'VALUE', expr(step.indexVar, ctx));
  }
  ir(inner);

  const b = mk(ctx, 'set_reference_entity_variable_for_AOB');
  setVar(b, 'VAR', step.target, ctx);
  cv(b, 'VALUE', inner);
  return ir(b);
}

function compilePushToArray(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'push_new_element_to_array');
  if (!b) return null;
  const g = mk(ctx, 'get_entity_list_variable');
  setVar(g, 'VAR', step.target, ctx);
  cv(b, 'ARRAY_INPUT', ir(g));

  if (step.index !== undefined) {
    cv(b, 'INDEX_INPUT', expr(step.index, ctx));
  }

  // Add per-field rows via the V2 mutator
  if (step.fields && typeof step.fields === 'object') {
    const aobEntity = findAobEntity(step.target, ctx);
    for (const [fieldName, fieldExpr] of Object.entries(step.fields)) {
      const fieldType = resolveAobFieldType(aobEntity, fieldName);
      // permissive=true so any value block connects; FINISHED_LOADING tightens the check
      b.addFieldRow_(fieldName, fieldType, fieldName, true);
      const slotName = 'FIELD_' + fieldName.replace(/[^a-zA-Z0-9_]/g, '_');
      cv(b, slotName, expr(fieldExpr as Expression, ctx));
    }
  }

  return ir(b);
}

function findAobEntity(target: string, ctx: Ctx): PageContextEntity | null {
  return ctx.pc.entityListReferences.find(e =>
    e.id === target || (e as any).aobPath === target
  ) || null;
}

function resolveAobFieldType(entity: PageContextEntity | null, fieldName: string): string {
  if (!entity) return 'String';
  const f = entity.fields.find(ef => ef.name === fieldName);
  if (!f) return 'String';
  switch (f.type) {
    case 'String': return 'String';
    case 'Number': return 'Number';
    case 'Boolean': return 'Boolean';
    case 'TimeStamp': return 'TimeStamp';
    default: return 'String';
  }
}

function compilePushToEntityList(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'push_to_entity_list');
  setVar(b, 'VAR', step.target, ctx);
  return ir(b);
}

function compileDeleteFromList(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'delete_from_entity_list');
  const ag = mk(ctx, 'get_entity_list_variable');
  setVar(ag, 'VAR', step.target, ctx);
  cv(b, 'ARRAY_INPUT', ir(ag));
  // INDEX: variable-vs-expression (2026-08-05). This was the ONLY one of the four index-taking compilers
  // here without the branch its three siblings have (compileSetEntityFromList / compileSetAobRef /
  // compileSetAobFromAob, above) — and unlike them it did not merely degrade, it THREW. A non-string
  // indexVar reaches setVar → isGeneralAobBlockTargetingAaob → encodedId.includes, which is not a
  // function on a number or an object; compileStep's catch then swallowed it and returned null, so the
  // WHOLE delete step vanished from the rebuilt workspace.
  //
  // Already live, not hypothetical: forge.prompts documents a literal number here ("prefer a literal
  // number directly" — DELETE_FROM_LIST is named in rule 23), the validator accepts it, and
  // forge-compiler.ts:544 compiles it. Only this half crashed, so every Forge plan deleting a fixed
  // index has been silently losing that step. The 2026-08-05 readIndex fix in blockly-to-steps makes it
  // reachable from the round trip too — a stored `delete item 3` now decompiles as 3 rather than ''.
  //
  // Verified headlessly (docs/testing/headless-blockly-compiler-tests.md): string, literal and
  // Expression all build a connected INDEX child, no errors, no strays.
  if (typeof step.indexVar === 'string') {
    const ig = mk(ctx, 'get_variable');
    setVar(ig, 'VAR', step.indexVar, ctx);
    cv(b, 'INDEX', ir(ig));
  } else if (step.indexVar !== undefined && step.indexVar !== null) {
    // `omit to delete last` is documented grammar — leave INDEX empty rather than planting a logic_null.
    cv(b, 'INDEX', expr(step.indexVar, ctx));
  }
  return ir(b);
}

function compileCallFunction(step: any, ctx: Ctx): any {
  return compileCallDynamic(`${step.name}_api_functions`, step, ctx);
}

function compileCallWorkflow(step: any, ctx: Ctx): any {
  return compileCallDynamic(`${step.name}_ap_workflow`, step, ctx);
}

function compileCallDynamic(blockType: string, step: any, ctx: Ctx): any {
  const b = mk(ctx, blockType);
  if (!b) return null;
  for (const [key, val] of Object.entries(step.inputs || {})) {
    cv(b, key, expr(val as Expression, ctx));
  }
  if (step.outputs) {
    for (const [key, encodedId] of Object.entries(step.outputs)) {
      const vt = resolveVarType(encodedId as string, ctx);
      const gt = GETTER_BLOCK_FOR_TYPE[vt] || 'get_variable';
      const g = mk(ctx, gt);
      setVar(g, 'VAR', encodedId as string, ctx);
      cv(b, key, ir(g));
    }
  }
  return ir(b);
}

/** The ZONE field for a step's trailing 'utc' (or TIME_DIFF's `zone`). Anything else — including absent — is project time. */
const utcField = (arg: unknown): Record<string, string> => (arg === 'utc' ? { ZONE: 'utc' } : {});

function compileTimeDiff(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'time_difference', { UNIT: step.unit || 'day', ...utcField(step.zone) });
  if (!b) return null;
  for (const [key, val] of Object.entries(step.inputs || {})) {
    cv(b, key, expr(val as Expression, ctx));
  }
  if (step.outputs) {
    for (const [key, encodedId] of Object.entries(step.outputs)) {
      const vt = resolveVarType(encodedId as string, ctx);
      const gt = GETTER_BLOCK_FOR_TYPE[vt] || 'get_variable';
      const g = mk(ctx, gt);
      setVar(g, 'VAR', encodedId as string, ctx);
      cv(b, key, ir(g));
    }
  }
  return ir(b);
}

/** check_location_access — same output-slot pattern as compileTimeDiff, minus the inputs. */
function compileCheckLocationAccess(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'check_location_access');
  if (!b) return null;
  if (step.outputs) {
    for (const [key, encodedId] of Object.entries(step.outputs)) {
      const vt = resolveVarType(encodedId as string, ctx);
      const gt = GETTER_BLOCK_FOR_TYPE[vt] || 'get_variable';
      const g = mk(ctx, gt);
      setVar(g, 'VAR', encodedId as string, ctx);
      cv(b, key, ir(g));
    }
  }
  return ir(b);
}

function compileAsync(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'async_function_wrapper');
  sf(b, 'ASYNC_GROUP', `async_group_${step.group}`);
  cs(b, 'TRY', chainSteps(step.try, ctx));
  if (step.catch?.length) cs(b, 'CATCH', chainSteps(step.catch, ctx));
  if (step.finally?.length) cs(b, 'FINALLY', chainSteps(step.finally, ctx));
  return ir(b);
}

function compileResolveAsync(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'resolve_promise_by_group');
  sf(b, 'ASYNC_GROUP', `async_group_${step.group}`);
  cs(b, 'BODY', chainSteps(step.body, ctx));
  return ir(b);
}

function compileToast(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'show_toast');
  sf(b, 'TOAST_TYPE', step.type || 'success');
  cv(b, 'MESSAGE', expr(step.message, ctx));
  cv(b, 'DURATION', expr(step.duration ?? 3000, ctx));
  return ir(b);
}

function compileNavigate(step: any, ctx: Ctx): any {
  const page = ctx.pc.pages.find(p => p.name === step.page || p.id === step.page);
  const b = mk(ctx, 'set_navigation');
  sf(b, 'VAR', page?.id || step.page);
  return ir(b);
}

function compileOpenExternal(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'open_external_string');
  cv(b, 'URL', expr(step.url, ctx));
  return ir(b);
}

function compileLoading(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'show_loading_screen');
  cv(b, 'showLoading', expr(step.show, ctx));
  return ir(b);
}

function compileReturn(step: any, ctx: Ctx): any {
  // The RETURN step is lane-agnostic by design; the lane decides which block carries it back.
  const b = mk(ctx, ctx.lane === 'value' ? 'return_value' : 'return_boolean');
  if (step.value !== undefined) cv(b, 'VALUE', expr(step.value, ctx));
  return ir(b);
}

function compileGetCurrentUser(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'get_current_user');
  const g = mk(ctx, 'get_entity_variable');
  setVar(g, 'VAR', step.destination, ctx);
  cv(b, 'user', ir(g));
  return ir(b);
}

function compileGetUsersList(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'get_users_list');
  const g = mk(ctx, 'get_entity_list_variable');
  setVar(g, 'VAR', step.destination, ctx);
  cv(b, 'users', ir(g));
  return ir(b);
}

function compileGetRolesList(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'get_roles_list');
  const g = mk(ctx, 'get_entity_list_variable');
  setVar(g, 'VAR', step.destination, ctx);
  cv(b, 'roles', ir(g));
  return ir(b);
}

function compileEditUser(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'edit_user');
  const g = mk(ctx, 'get_entity_variable');
  setVar(g, 'VAR', step.entityVar, ctx);
  cv(b, 'VARIABLE', ir(g));
  return ir(b);
}

function compileTextAppend(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'text_append');
  setVar(b, 'VAR', step.target, ctx);
  cv(b, 'TEXT', expr(step.text, ctx));
  return ir(b);
}

function compileRepeat(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'controls_repeat_ext');
  cv(b, 'TIMES', expr(step.times, ctx));
  cs(b, 'DO', chainSteps(step.body, ctx));
  return ir(b);
}

function compileWhileUntil(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'controls_whileUntil');
  sf(b, 'MODE', step.mode || 'WHILE');
  cv(b, 'BOOL', expr(step.condition, ctx));
  cs(b, 'DO', chainSteps(step.body, ctx));
  return ir(b);
}

function compileSetAobField(step: any, ctx: Ctx): any {
  const vt = resolveVarType(step.target, ctx);
  const bt = AOB_SETTER_FOR_TYPE[vt] || 'set_aob_variable';
  const b = mk(ctx, bt);
  setVar(b, 'VAR', step.target, ctx);
  cv(b, 'VALUE', expr(step.value, ctx));
  return ir(b);
}

function compileSetAaobKeyValue(step: any, ctx: Ctx): any {
  const b = mk(ctx, 'set_key_value_in_AOB_reference');
  setVar(b, 'VAR', step.target, ctx);
  // AAOB_FIELD + TYPE added 2026-08-04, mirroring compileAaobAggregateQuery which always set them.
  // Without AAOB_FIELD the code generator's guard is false and the block emits NO upsert call — the
  // function compiles, runs and silently writes nothing. TYPE defaults to String (the only type the
  // dropdown offers today) and is half the (systemKey, systemType) match key.
  if (step.aaobField) sf(b, 'AAOB_FIELD', step.aaobField);   // guarded: sf stringifies, so an absent
  sf(b, 'TYPE', AAOB_VALUE_TYPE);                            // decided by code, not by the step — see AAOB_VALUE_TYPE
  cv(b, 'KEY', expr(step.key, ctx));
  cv(b, 'VALUE', expr(step.value, ctx));
  return ir(b);
}

function compileAaobAggregateQuery(step: any, ctx: Ctx): any {
  const entity = findEntity(step.entity, ctx);
  const entityId = entity?.id || step.entity;

  const a = mk(ctx, 'aaob_aggregate_by_label');
  sf(a, 'VAR', entityId);
  sf(a, 'AAOB_FIELD', step.aaobField);
  sf(a, 'OP', step.op);
  cv(a, 'KEY', expr(step.key, ctx));
  if (step.conditions?.length) cv(a, 'CONDITIONS', compileConds(step.conditions, entity, ctx));
  // Deprecated: childEntities replaced by imperative conditions
  // if (step.childEntities?.length) cv(a, 'EC', compileChildEnts(step.childEntities, ctx));
  ir(a);

  const b = mk(ctx, 'set_variable');
  setVar(b, 'VAR', step.destination, ctx);
  cv(b, 'VALUE', a);
  return ir(b);
}

// === Expression Compiler ===

function expr(e: Expression, ctx: Ctx): any | null {
  if (e === null || e === undefined) return ir(mk(ctx, 'logic_null'));
  if (typeof e === 'string') return ir(mk(ctx, 'text', { TEXT: e }));
  if (typeof e === 'number') return ir(mk(ctx, 'math_number', { NUM: e }));
  if (typeof e === 'boolean') return ir(mk(ctx, 'logic_boolean', { BOOL: e ? 'TRUE' : 'FALSE' }));

  if (isVarRef(e)) return exprVar(e, ctx);
  if (isBinaryOp(e)) return exprBinary(e, ctx);
  if (isUnaryOp(e)) return exprUnary(e, ctx);
  if (isFnCall(e)) return exprFn(e, ctx);

  ctx.warnings.push(`Unknown expression: ${JSON.stringify(e)}`);
  return ir(mk(ctx, 'text', { TEXT: String(e) }));
}

function exprVar(e: ExpressionVar, ctx: Ctx): any {
  const vt = resolveVarType(e.var, ctx);
  const gt = GETTER_BLOCK_FOR_TYPE[vt] || 'get_variable';
  const b = mk(ctx, gt);
  setVar(b, 'VAR', e.var, ctx);
  return ir(b);
}

const BINARY_OP_MAP: Record<string, { bt: string; fn: string; fv: string }> = {
  ADD: { bt: 'math_arithmetic', fn: 'OP', fv: 'ADD' },
  MINUS: { bt: 'math_arithmetic', fn: 'OP', fv: 'MINUS' },
  MULTIPLY: { bt: 'math_arithmetic', fn: 'OP', fv: 'MULTIPLY' },
  DIVIDE: { bt: 'math_arithmetic', fn: 'OP', fv: 'DIVIDE' },
  POWER: { bt: 'math_arithmetic', fn: 'OP', fv: 'POWER' },
  MODULO: { bt: 'math_modulo', fn: 'OP', fv: 'MODULO' },
  EQ: { bt: 'logic_compare', fn: 'OP', fv: 'EQ' },
  NEQ: { bt: 'logic_compare', fn: 'OP', fv: 'NEQ' },
  LT: { bt: 'logic_compare', fn: 'OP', fv: 'LT' },
  LTE: { bt: 'logic_compare', fn: 'OP', fv: 'LTE' },
  GT: { bt: 'logic_compare', fn: 'OP', fv: 'GT' },
  GTE: { bt: 'logic_compare', fn: 'OP', fv: 'GTE' },
  AND: { bt: 'logic_operation', fn: 'OP', fv: 'AND' },
  OR: { bt: 'logic_operation', fn: 'OP', fv: 'OR' },
};

function exprBinary(e: ExpressionBinaryOp, ctx: Ctx): any {
  const m = BINARY_OP_MAP[e.op];
  if (!m) { ctx.warnings.push(`Unknown binary op: ${e.op}`); return expr(e.left, ctx); }

  const b = mk(ctx, m.bt);
  if (m.bt === 'math_modulo') {
    cv(b, 'DIVIDEND', expr(e.left, ctx));
    cv(b, 'DIVISOR', expr(e.right, ctx));
  } else {
    sf(b, m.fn, m.fv);
    cv(b, 'A', expr(e.left, ctx));
    cv(b, 'B', expr(e.right, ctx));
  }
  return ir(b);
}

const UNARY_OP_MAP: Record<string, { bt: string; fn: string; fv: string; inp: string }> = {
  NOT: { bt: 'logic_negate', fn: '', fv: '', inp: 'BOOL' },
  NEG: { bt: 'math_single', fn: 'OP', fv: 'NEG', inp: 'NUM' },
  ABS: { bt: 'math_single', fn: 'OP', fv: 'ABS', inp: 'NUM' },
  ROOT: { bt: 'math_single', fn: 'OP', fv: 'ROOT', inp: 'NUM' },
  LN: { bt: 'math_single', fn: 'OP', fv: 'LN', inp: 'NUM' },
  LOG10: { bt: 'math_single', fn: 'OP', fv: 'LOG10', inp: 'NUM' },
  EXP: { bt: 'math_single', fn: 'OP', fv: 'EXP', inp: 'NUM' },
  ROUND: { bt: 'math_round', fn: 'OP', fv: 'ROUND', inp: 'NUM' },
  ROUNDUP: { bt: 'math_round', fn: 'OP', fv: 'ROUNDUP', inp: 'NUM' },
  ROUNDDOWN: { bt: 'math_round', fn: 'OP', fv: 'ROUNDDOWN', inp: 'NUM' },
};

function exprUnary(e: ExpressionUnaryOp, ctx: Ctx): any {
  const m = UNARY_OP_MAP[e.op];
  if (!m) { ctx.warnings.push(`Unknown unary op: ${e.op}`); return expr(e.arg, ctx); }
  const b = mk(ctx, m.bt);
  if (m.fn) sf(b, m.fn, m.fv);
  cv(b, m.inp, expr(e.arg, ctx));
  return ir(b);
}

const FN_MAP: Record<string, (args: Expression[], ctx: Ctx) => any> = {
  text_join: (a, ctx) => {
    const b = mk(ctx, 'text_join');
    b.itemCount_ = a.length; b.updateShape_();
    for (let i = 0; i < a.length; i++) cv(b, `ADD${i}`, expr(a[i], ctx));
    return ir(b);
  },
  text_length: (a, ctx) => { const b = mk(ctx, 'text_length'); cv(b, 'VALUE', expr(a[0], ctx)); return ir(b); },
  text_isEmpty: (a, ctx) => { const b = mk(ctx, 'text_isEmpty'); cv(b, 'VALUE', expr(a[0], ctx)); return ir(b); },
  text_indexOf: (a, ctx) => {
    // END was hardcoded 'FIRST' here too — this is the compiler the APP uses, so this is the half that
    // actually rewrote users' saved blocks. Same shape as text_charAt's WHERE on the next line.
    const b = mk(ctx, 'text_indexOf', { END: (a[2] as string) || 'FIRST' });
    cv(b, 'VALUE', expr(a[0], ctx)); cv(b, 'FIND', expr(a[1], ctx)); return ir(b);
  },
  text_charAt: (a, ctx) => {
    const b = mk(ctx, 'text_charAt', { WHERE: (a[2] as string) || 'FROM_START' });
    cv(b, 'VALUE', expr(a[0], ctx)); cv(b, 'AT', expr(a[1], ctx)); return ir(b);
  },
  text_getSubstring: (a, ctx) => {
    // WHERE1/WHERE2 were never set, so every saved FROM_END/FIRST/LAST collapsed to FROM_START. They go
    // through `mk`, i.e. setFieldValue, which fires the dropdown validator and reshapes the block FIRST —
    // so by the time cv() runs, an AT input that this setting removes is already gone and cv no-ops on it
    // (it guards on getInput). Ordering is the whole fix here; the connections must stay after the fields.
    const b = mk(ctx, 'text_getSubstring', {
      WHERE1: (a[3] as string) || 'FROM_START',
      WHERE2: (a[4] as string) || 'FROM_START',
    });
    cv(b, 'STRING', expr(a[0], ctx)); cv(b, 'AT1', expr(a[1], ctx)); cv(b, 'AT2', expr(a[2], ctx)); return ir(b);
  },
  text_changeCase: (a, ctx) => {
    const b = mk(ctx, 'text_changeCase', { CASE: (a[1] as string) || 'UPPERCASE' });
    cv(b, 'TEXT', expr(a[0], ctx)); return ir(b);
  },
  text_trim: (a, ctx) => {
    const b = mk(ctx, 'text_trim', { MODE: (a[1] as string) || 'BOTH' });
    cv(b, 'TEXT', expr(a[0], ctx)); return ir(b);
  },
  value_is_empty: (a, ctx) => {
    const b = mk(ctx, 'value_is_empty');
    cv(b, 'VALUE', expr(a[0], ctx)); return ir(b);
  },
  string_contains: (a, ctx) => {
    const b = mk(ctx, 'string_contains');
    cv(b, 'TEXT', expr(a[0], ctx)); cv(b, 'SUBSTRING', expr(a[1], ctx)); return ir(b);
  },
  string_regex_match: (a, ctx) => {
    const b = mk(ctx, 'string_regex_match');
    cv(b, 'TEXT', expr(a[0], ctx)); cv(b, 'REGEX', expr(a[1], ctx)); return ir(b);
  },
  polygon_area: (a, ctx) => {
    const b = mk(ctx, 'polygon_area', { UNIT: (a[1] as string) || 'hectares' });
    cv(b, 'GEOMETRY', expr(a[0], ctx)); return ir(b);
  },
  reverse_geocode: (a, ctx) => {
    const b = mk(ctx, 'reverse_geocode');
    cv(b, 'GEOMETRY', expr(a[0], ctx)); return ir(b);
  },
  point_count: (a, ctx) => {
    const b = mk(ctx, 'point_count');
    cv(b, 'GEOMETRY', expr(a[0], ctx)); return ir(b);
  },
  format_iso_date: (a, ctx) => { const b = mk(ctx, 'format_iso_date', utcField(a[2])); cv(b, 'DATE', expr(a[0], ctx)); if (a[1] !== undefined) cv(b, 'FORMAT', expr(a[1], ctx)); return ir(b); },
  parse_date_to_iso: (a, ctx) => { const b = mk(ctx, 'parse_date_to_iso', { FORMAT: (a[1] as string) || 'YYYY-MM-DD', ...utcField(a[2]) }); cv(b, 'VALUE', expr(a[0], ctx)); return ir(b); }, // FORMAT is a dropdown field now
  current_date_iso: (_a, ctx) => ir(mk(ctx, 'current_date_iso')),
  date_start_of_month: (a, ctx) => { const b = mk(ctx, 'date_start_of_month', utcField(a[1])); cv(b, 'DATE', expr(a[0], ctx)); return ir(b); },
  date_end_of_month: (a, ctx) => { const b = mk(ctx, 'date_end_of_month', utcField(a[1])); cv(b, 'DATE', expr(a[0], ctx)); return ir(b); },
  date_start_of_day: (a, ctx) => { const b = mk(ctx, 'date_start_of_day', utcField(a[1])); cv(b, 'DATE', expr(a[0], ctx)); return ir(b); },
  date_end_of_day: (a, ctx) => { const b = mk(ctx, 'date_end_of_day', utcField(a[1])); cv(b, 'DATE', expr(a[0], ctx)); return ir(b); },
  date_add_days: (a, ctx) => {
    // Was (2026-09-19): const b = mk(ctx, 'date_add_days', { UNIT: (a[2] as string) || 'day', ...utcField(a[3]) });
    // ⚠ The prompt allows `date_add_days(d, n, 'utc')` — the zone with no unit. 'utc' is not a UNIT (the
    // dropdown rejects it, leaving the block on project time), so a third 'utc' is the zone, in days.
    const [unit, zone] = a[2] === 'utc' ? ['day', a[2]] : [(a[2] as string) || 'day', a[3]];
    const b = mk(ctx, 'date_add_days', { UNIT: unit, ...utcField(zone) });
    cv(b, 'DATE', expr(a[0], ctx)); cv(b, 'DAYS', expr(a[1], ctx)); return ir(b);
  },
  // set_date / set_time are VALUE fns (not steps): their number args are inline FIELDS, so they map to
  // mk() field values, not value sockets. build_date(day,month,year[,'utc']); set_time_of_day(date,hour,minute[,'utc']).
  build_date: (a, ctx) => ir(mk(ctx, 'set_date', { DAY: String(a[0] ?? 1), MONTH: String(a[1] ?? 1), YEAR: String(a[2] ?? 2026), ...utcField(a[3]) })),
  set_time_of_day: (a, ctx) => {
    const b = mk(ctx, 'set_time', { HOUR: String(a[1] ?? 0), MINUTE: String(a[2] ?? 0), ...utcField(a[3]) });
    cv(b, 'DATE', expr(a[0], ctx)); return ir(b);
  },
  date_diff_hours: (a, ctx) => {
    const b = mk(ctx, 'date_diff_hours');
    cv(b, 'DATE_A', expr(a[0], ctx)); cv(b, 'DATE_B', expr(a[1], ctx)); return ir(b);
  },
  date_diff_minutes: (a, ctx) => {
    const b = mk(ctx, 'date_diff_minutes');
    cv(b, 'DATE_A', expr(a[0], ctx)); cv(b, 'DATE_B', expr(a[1], ctx)); return ir(b);
  },
  trim_decimals: (a, ctx) => {
    const b = mk(ctx, 'trim_decimals');
    cv(b, 'VALUE', expr(a[0], ctx)); cv(b, 'DIGITS', expr(a[1], ctx)); return ir(b);
  },
  number_to_words: (a, ctx) => { const b = mk(ctx, 'number_to_words'); cv(b, 'NUM', expr(a[0], ctx)); return ir(b); },
  math_constrain: (a, ctx) => {
    const b = mk(ctx, 'math_constrain');
    cv(b, 'VALUE', expr(a[0], ctx)); cv(b, 'LOW', expr(a[1], ctx)); cv(b, 'HIGH', expr(a[2], ctx)); return ir(b);
  },
  math_random_int: (a, ctx) => {
    const b = mk(ctx, 'math_random_int');
    cv(b, 'FROM', expr(a[0], ctx)); cv(b, 'TO', expr(a[1], ctx)); return ir(b);
  },
  entity_length: (a, ctx) => {
    const varId = isVarRef(a[0] as Expression) ? (a[0] as ExpressionVar).var : String(a[0]);
    const b = mk(ctx, 'get_entity_length');
    setVar(b, 'VAR', varId, ctx);
    return ir(b);
  },
  get_email: (_a, ctx) => ir(mk(ctx, 'get_email')),
  // auth / current-user / tenant VALUE blocks — forward hydration (2026-07-26), mirrors blockly-to-steps arg order
  get_current_user_role: (_a, ctx) => ir(mk(ctx, 'get_current_user_role')),
  app_version: (_a, ctx) => ir(mk(ctx, 'app_version')),
  auth_login: (a, ctx) => { const b = mk(ctx, 'auth_login'); cv(b, 'EMAIL', expr(a[0], ctx)); cv(b, 'PASSWORD', expr(a[1], ctx)); return ir(b); },
  auth_send_password_reset: (a, ctx) => { const b = mk(ctx, 'auth_send_password_reset'); cv(b, 'EMAIL', expr(a[0], ctx)); return ir(b); },
  auth_signup_found: (a, ctx) => { const b = mk(ctx, 'auth_signup_found'); cv(b, 'EMAIL', expr(a[0], ctx)); cv(b, 'PASSWORD', expr(a[1], ctx)); cv(b, 'USERNAME', expr(a[2], ctx)); cv(b, 'TENANTNAME', expr(a[3], ctx)); return ir(b); },
  tenant_add_user: (a, ctx) => { const b = mk(ctx, 'tenant_add_user'); cv(b, 'EMAIL', expr(a[0], ctx)); cv(b, 'USERNAME', expr(a[1], ctx)); cv(b, 'ROLE', expr(a[2], ctx)); return ir(b); },
  tenant_cancel_invite: (a, ctx) => { const b = mk(ctx, 'tenant_cancel_invite'); cv(b, 'EMAIL', expr(a[0], ctx)); return ir(b); },
  tenant_edit_user_role: (a, ctx) => { const b = mk(ctx, 'tenant_edit_user_role'); cv(b, 'EMAIL', expr(a[0], ctx)); cv(b, 'USERNAME', expr(a[1], ctx)); cv(b, 'ROLE', expr(a[2], ctx)); return ir(b); },
  user_create_account: (a, ctx) => { const b = mk(ctx, 'user_create_account'); try { b.setFieldValue(String(a[0] ?? 'email'), 'MODE'); } catch {} cv(b, 'VALUE', expr(a[1], ctx)); cv(b, 'PASSWORD', expr(a[2], ctx)); cv(b, 'ROLE', expr(a[3], ctx)); return ir(b); },
  user_set_email: (a, ctx) => { const b = mk(ctx, 'user_set_email'); cv(b, 'IDENTIFIER', expr(a[0], ctx)); cv(b, 'EMAIL', expr(a[1], ctx)); return ir(b); },
  user_set_username: (a, ctx) => { const b = mk(ctx, 'user_set_username'); cv(b, 'IDENTIFIER', expr(a[0], ctx)); cv(b, 'USERNAME', expr(a[1], ctx)); return ir(b); },
  user_reset_password: (a, ctx) => { const b = mk(ctx, 'user_reset_password'); cv(b, 'IDENTIFIER', expr(a[0], ctx)); cv(b, 'NEW_PASSWORD', expr(a[1], ctx)); return ir(b); },
  user_change_password: (a, ctx) => { const b = mk(ctx, 'user_change_password'); cv(b, 'CURRENT', expr(a[0], ctx)); cv(b, 'NEW', expr(a[1], ctx)); return ir(b); },
  // People blocks (Plan 3) — the inverse of blockly-to-steps.ts.
  person_remove: (a, ctx) => peopleBlockFromStep('person_remove', a, ctx),
  person_reinstate: (a, ctx) => peopleBlockFromStep('person_reinstate', a, ctx),
  person_erase: (a, ctx) => peopleBlockFromStep('person_erase', a, ctx),
  user_delete_account: (a, ctx) => {
    const b = mk(ctx, 'user_delete_account');
    if (!b) return null;
    setPeopleTheme(b, a[0], a[1], a[2]);
    return ir(b);
  },
  person_transfer: (a, ctx) => {
    const b = mk(ctx, 'person_transfer');
    if (!b) return null;
    cv(b, 'FROM', expr(a[0], ctx));
    cv(b, 'TO', expr(a[1], ctx));
    // NO LIST MEANS ALL (Plan 4, 2026-10-04). Forge writes only the two people, so a step without a third
    // argument keeps the block's own default — every entity that points at people, ticked. An EMPTY list
    // ('') is different: it is what a builder's block with nothing ticked decompiles to, and that choice is
    // kept. Headless (Drydock) the block knows no entities, so a missing list rebuilds with none ticked and
    // the server refuses the transfer by name — accepted, Plan 4 is Forge driven from the editor.
    if (a[2] !== undefined && a[2] !== null) {
      b.setChosenEntities_?.(String(a[2]).split(',').map((s: string) => s.trim()).filter(Boolean));
    }
    setPeopleTheme(b, a[3], a[4], a[5]);
    return ir(b);
  },
  // delete record — the inverse of blockly-to-steps.ts's 'delete_record' case.
  record_delete: (a, ctx) => {
    const b = mk(ctx, 'delete_record');
    if (!b) return null;
    cv(b, 'RECORD', expr(a[0], ctx));
    sf(b, 'ASK', a[1] === 'FALSE' ? 'FALSE' : 'TRUE');
    return ir(b);
  },
  // Facebook page leads — the inverse of blockly-to-steps.ts's 'meta_page_subscription' case.
  meta_page_subscription: (a, ctx) => {
    const b = mk(ctx, 'meta_page_subscription');
    if (!b) return null;
    cv(b, 'PAGE', expr(a[0], ctx));
    sf(b, 'ACTION', a[1] === 'subscribe' ? 'subscribe' : 'unsubscribe');
    return ir(b);
  },
  ternary: (a, ctx) => {
    const b = mk(ctx, 'logic_ternary');
    cv(b, 'IF', expr(a[0], ctx)); cv(b, 'THEN', expr(a[1], ctx)); cv(b, 'ELSE', expr(a[2], ctx)); return ir(b);
  },
  math_trig: (a, ctx) => {
    const b = mk(ctx, 'math_trig', { OP: (a[1] as string) || 'SIN' });
    cv(b, 'NUM', expr(a[0], ctx)); return ir(b);
  },
  math_constant: (a, ctx) => ir(mk(ctx, 'math_constant', { CONSTANT: (a[0] as string) || 'PI' })),
  math_number_property: (a, ctx) => {
    const b = mk(ctx, 'math_number_property', { PROPERTY: (a[1] as string) || 'EVEN' });
    cv(b, 'NUMBER_TO_CHECK', expr(a[0], ctx)); return ir(b);
  },
  math_on_list: (a, ctx) => {
    const b = mk(ctx, 'math_on_list', { OP: (a[1] as string) || 'SUM' });
    cv(b, 'LIST', expr(a[0], ctx)); return ir(b);
  },
  math_random_float: (_a, ctx) => ir(mk(ctx, 'math_random_float')),
  lists_create_with: (a, ctx) => {
    const b = mk(ctx, 'lists_create_with');
    b.itemCount_ = a.length; b.updateShape_();
    for (let i = 0; i < a.length; i++) cv(b, `ADD${i}`, expr(a[i], ctx));
    return ir(b);
  },
  text_prompt_ext: (a, ctx) => {
    const b = mk(ctx, 'text_prompt_ext', { TYPE: (a[1] as string) || 'TEXT' });
    cv(b, 'TEXT', expr(a[0], ctx)); return ir(b);
  },
  get_aob_field: (a, _ctx) => {
    const varId = isVarRef(a[0] as Expression) ? (a[0] as ExpressionVar).var : String(a[0]);
    const fieldType = (a[1] as string) || 'String';
    const bt = AOB_GETTER_FOR_TYPE[fieldType] || 'get_aob_variable';
    const b = mk(_ctx, bt);
    setVar(b, 'VAR', varId, _ctx);
    return ir(b);
  },
};

function exprFn(e: ExpressionFnCall, ctx: Ctx): any {
  const compiler = FN_MAP[e.fn];
  if (compiler) return compiler(e.args, ctx);
  ctx.warnings.push(`Unknown function: ${e.fn}`);
  return ir(mk(ctx, 'text', { TEXT: `[unknown fn: ${e.fn}]` }));
}

// === Condition Compiler ===

function compileConds(
  conditions: QueryConditionItem[],
  entity: PageContextEntity | null,
  ctx: Ctx
): any | null {
  if (conditions.length === 0) return null;

  let head: any = null;
  for (let i = conditions.length - 1; i >= 0; i--) {
    const item = conditions[i];

    if (isOrCondition(item)) {
      head = compileOrCond(item, entity, ctx);
    } else if (isAndCondition(item)) {
      head = compileAndCond(item, entity, ctx);
    } else if (isAaobFilter(item)) {
      head = compileAaobFilterCond(item, head, ctx);
    } else if (isAaobLabelExists(item)) {
      head = compileAaobLabelExistsCond(item, head, ctx);
    } else {
      head = compileSimpleCond(item, entity, head, ctx);
    }
  }

  return head;
}

function compileSimpleCond(
  cond: QueryCondition,
  entity: PageContextEntity | null,
  sibling: any,
  ctx: Ctx
): any {
  const field = resolveEntityField(entity, cond.field);
  const fieldType = field ? getFieldPrimType(field.type) : 'String';
  const condVarId = entity ? cond.field + '$' + entity.id : (field?.encodedId || cond.field);
  const setterType = CONDITION_SETTER_FOR_TYPE[fieldType] || 'entity_set_string_in_conditions';

  const setter = mk(ctx, setterType);
  sf(setter, 'VAR', condVarId);

  if (cond.op === '_is_null') {
    const nullBlock = mk(ctx, 'entity_is_null_conditions');
    sf(nullBlock, 'DROPDOWN', cond.value ? 'true' : 'false');
    cv(setter, 'SET', ir(nullBlock));
  } else {
    const condType = CONDITION_BLOCK_FOR_TYPE[fieldType] || 'entity_string_conditions';
    const condBlock = mk(ctx, condType);
    sf(condBlock, 'DROPDOWN', (typeof cond.op === 'string' ? cond.op : '_eq') as string);
    cv(condBlock, 'SET', expr(cond.value, ctx));
    cv(setter, 'SET', ir(condBlock));
  }

  if (sibling) cv(setter, 'SIBILING_CONDITIONS', sibling);
  return ir(setter);
}

function compileOrCond(cond: QueryOrCondition, entity: PageContextEntity | null, ctx: Ctx): any | null {
  const branches = cond.or.map(g => compileConds(g, entity, ctx)).filter(Boolean);
  if (branches.length < 2) return branches[0] || null;
  let current = mkBranch(ctx, 'entity_or_conditions', branches[0], branches[1]);
  for (let i = 2; i < branches.length; i++) {
    current = mkBranch(ctx, 'entity_or_conditions', current, branches[i]);
  }
  return current;
}

function compileAndCond(cond: QueryAndCondition, entity: PageContextEntity | null, ctx: Ctx): any | null {
  const branches = cond.and.map(g => compileConds(g, entity, ctx)).filter(Boolean);
  if (branches.length < 2) return branches[0] || null;
  let current = mkBranch(ctx, 'entity_and_conditions', branches[0], branches[1]);
  for (let i = 2; i < branches.length; i++) {
    current = mkBranch(ctx, 'entity_and_conditions', current, branches[i]);
  }
  return current;
}

function mkBranch(ctx: Ctx, blockType: string, child: any, sibling: any): any {
  const b = mk(ctx, blockType);
  if (blockType === 'entity_and_conditions') {
    // entity_and_conditions uses statement inputs
    cs(b, 'CHILD_CONDITIONS', child);
    cs(b, 'SIBILING_CONDITIONS', sibling);
  } else {
    // entity_or_conditions uses value inputs
    cv(b, 'CHILD_CONDITIONS', child);
    cv(b, 'SIBILING_CONDITIONS', sibling);
  }
  return ir(b);
}

function compileAaobFilterCond(cond: QueryAaobFilter, sibling: any, ctx: Ctx): any {
  const b = mk(ctx, 'aaob_filter_by_label');
  sf(b, 'VAR', cond.aaobField);
  sf(b, 'OP', cond.op);
  sf(b, 'TYPE', AAOB_VALUE_TYPE);
  cv(b, 'KEY', expr(cond.key, ctx));
  cv(b, 'VALUE', expr(cond.value, ctx));
  if (sibling) cv(b, 'SIBILING_CONDITIONS', sibling);
  return ir(b);
}

function compileAaobLabelExistsCond(cond: QueryAaobLabelExists, sibling: any, ctx: Ctx): any {
  const b = mk(ctx, 'aaob_label_exists');
  sf(b, 'VAR', cond.aaobField);
  cv(b, 'KEY', expr(cond.key, ctx));
  if (sibling) cv(b, 'SIBILING_CONDITIONS', sibling);
  return ir(b);
}

// === Child Entities ===
// Deprecated: childEntities replaced by imperative conditions (add_condition on child entity target)

// function compileChildEnts(children: QueryChildEntity[], ctx: Ctx): any | null {
//   if (children.length === 0) return null;
//   const child = children[0];
//   const entity = findEntity(child.childEntity, ctx);
//   const entityId = entity?.id || child.childEntity;
//
//   const b = mk(ctx, 'entity_conditions');
//   sf(b, 'VAR', entityId);
//
//   if (child.orderBy) {
//     const o = mk(ctx, 'get_set_order_variable');
//     sf(o, 'VAR', child.orderBy.field + '$' + entityId);
//     sf(o, 'DROPDOWN', child.orderBy.direction === 'desc' ? 'desc' : 'asc');
//     cv(b, 'B', ir(o));
//   }
//   if (child.limit !== undefined) cv(b, 'C', expr(child.limit, ctx));
//   if (child.conditions.length > 0) cv(b, 'CONDITIONS', compileConds(child.conditions, entity, ctx));
//
//   return ir(b);
// }

// === Imperative Condition/Order Blocks (Phase 3/3.5) ===
// Mirrors forge-compiler.ts compileImperativeConditions / compileImperativeOrders
// but produces real Blockly blocks instead of BlockJSON.

function chainStatementBlocks(blocks: any[]): any | null {
  const live = blocks.filter(Boolean);
  if (live.length === 0) return null;
  for (let i = 0; i < live.length - 1; i++) {
    if (live[i].nextConnection && live[i + 1].previousConnection) {
      try { live[i].nextConnection.connect(live[i + 1].previousConnection); } catch { /* skip */ }
    }
  }
  return live[0];
}

function collectConditionTargets(conditions: QueryConditionItem[], defaultOn: string): string[] {
  const targets = new Set<string>();
  targets.add(defaultOn);
  function walk(items: QueryConditionItem[]): void {
    for (const item of items) {
      if (isOrCondition(item)) { for (const branch of item.or) walk(branch); }
      else if (isAndCondition(item)) { for (const branch of item.and) walk(branch); }
      else if (!isAaobFilter(item) && !isAaobLabelExists(item) && (item as QueryCondition).on) {
        targets.add((item as QueryCondition).on as string);
      }
    }
  }
  walk(conditions);
  return [...targets];
}

function mkClearBlock(blockType: 'clear_conditions' | 'clear_orders', target: string, ctx: Ctx): any {
  const b = mk(ctx, blockType);
  if (!b) return null;
  b.data = JSON.stringify({ onPath: target });
  sf(b, 'TARGET', target);
  return ir(b);
}

function emitImperativeConditionBlocks(
  conditions: QueryConditionItem[], defaultOn: string, ctx: Ctx
): any[] {
  const targets = collectConditionTargets(conditions, defaultOn);
  const clears = targets.map(t => mkClearBlock('clear_conditions', t, ctx)).filter(Boolean);
  const out: any[] = [...clears];
  for (const item of conditions) out.push(...emitConditionItemBlocks(item, defaultOn, 'AND', ctx));
  return out;
}

function emitConditionItemBlocks(
  item: QueryConditionItem, defaultOn: string, connector: 'AND' | 'OR', ctx: Ctx
): any[] {
  // Was (until 2026-10-06): branches flattened with || on each branch's first leaf — under in-order that reads (A&B|C)&D.
  // if (isOrCondition(item)) {
  //   const on = firstLeafOnImp(item.or.flat(), defaultOn);
  //   const body: any[] = [];
  //   item.or.forEach((branch, bi) => branch.forEach((leaf, li) => {
  //     const structConn: 'AND' | 'OR' = bi > 0 && li === 0 ? 'OR' : 'AND';
  //     body.push(mkAddConditionBlock(leaf, on, leaf.connector ?? structConn, ctx));
  //   }));
  //   return [mkConditionGroupBlock(connector, on, body, ctx)];
  // }
  if (isOrCondition(item)) {
    const on = firstLeafOnImp(item.or.flat(), defaultOn);
    const body: any[] = [];
    for (const part of orBranchParts(item.or)) {
      if (part.group) {
        const inner: any[] = [];
        for (const sub of part.items) inner.push(...emitConditionItemBlocks(sub, on, 'AND', ctx));
        body.push(mkConditionGroupBlock(part.connector, on, inner, ctx));
      } else {
        body.push(...emitConditionItemBlocks(part.items[0], on, part.connector, ctx));
      }
    }
    return [mkConditionGroupBlock(connector, on, body, ctx)];
  }
  if (isAndCondition(item)) {
    const flat = item.and.flat();
    const on = firstLeafOnImp(flat, defaultOn);
    const body: any[] = [];
    for (const sub of flat) body.push(...emitConditionItemBlocks(sub, on, 'AND', ctx));
    return [mkConditionGroupBlock(connector, on, body, ctx)];
  }
  if (isAaobFilter(item)) return [mkAaobAddConditionBlock(item, defaultOn, connector, ctx)];
  if (isAaobLabelExists(item)) return [mkAaobLabelExistsCondBlock(item, defaultOn, connector, ctx)];
  const leaf = item as QueryCondition;
  return [mkAddConditionBlock(leaf, defaultOn, leaf.connector ?? connector, ctx)];
}

function firstLeafOnImp(items: QueryConditionItem[], fallback: string): string {
  for (const it of items) {
    if (isOrCondition(it)) { const o = firstLeafOnImp(it.or.flat(), ''); if (o) return o; }
    else if (isAndCondition(it)) { const o = firstLeafOnImp(it.and.flat(), ''); if (o) return o; }
    else if (!isAaobFilter(it) && !isAaobLabelExists(it) && (it as QueryCondition).on) return (it as QueryCondition).on as string;
  }
  return fallback;
}

function opToFieldImp(op: string, value: Expression): string {
  if (op === '_is_null') return value ? '_isnull_true' : '_isnull_false';
  return op;
}

function isDynExpr(v: any): boolean {
  return typeof v === 'object' && v !== null;
}

function mkAddConditionBlock(
  cond: QueryCondition, defaultOn: string,
  connector: string | Expression, ctx: Ctx
): any {
  const on = cond.on || defaultOn;
  const isAaob = !!cond.aaobKey;
  const vt = AAOB_VALUE_TYPE;   // never cond.aaobType — see AAOB_VALUE_TYPE
  const data: any = { onPath: on, source: isAaob ? 'aaob' : 'field' };
  let wholeDays = false;
  if (isAaob) { data.aaobField = cond.field; data.dataType = vt; }
  else {
    data.path = cond.field.split('.').filter(Boolean);
    // When the value is a reference to an ENTITY_VARIABLE global, tag the block as ID-typed
    // so the code generator (condition-blocks.ts:305) emits `.data.id` instead of the raw
    // entity object. Without this, the compiled code passes {type:'ENTITY', data:{…}} as the
    // Hasura condition value, which the leafToWhere guard catches but shouldn't need to.
    if (!isDynExpr(cond.op) && cond.op !== '_is_null' && isVarRef(cond.value)) {
      const valType = resolveVarType((cond.value as ExpressionVar).var, ctx);
      if (valType === 'ENTITY_VARIABLE') data.dataType = 'ID';
    }
    // between (2026-10-07): the OP dropdown offers _between only for a block whose data names a Number or
    // Date & time field. A Forge-built condition carries no dataType, so without this stamp the dropdown
    // refuses _between and the block silently reads `=`. Never stamped for any other operator.
    if (cond.op === '_between') {
      const t = betweenFieldType(cond, defaultOn, ctx);
      if (t.type) { data.dataType = t.type; ctx.warnings.push(...betweenEndWarnings(cond, t.type)); }
      else ctx.warnings.push(`between on "${cond.field}" needs a Number or Date & time field (found ${t.seen}) — the block will read as =`);
    }
    // Whole days (2026-10-09): the block offers it only on a Date & time field, so a Forge-built block must carry that
    // type in its data, or the tick stays hidden and the generator ignores it.
    if (cond.wholeDays && cond.op !== '_is_null') {
      const t = betweenFieldType(cond, defaultOn, ctx);
      if (t.type === 'TimeStamp') {
        data.dataType = 'TimeStamp';
        wholeDays = true;
        // Was (until 2026-10-09): if (cond.op !== '_between') ctx.warnings.push(...betweenEndWarnings(cond, 'TimeStamp'));
        // (worded as "between … from value"). A between condition already got its end warnings above.
        if (cond.op !== '_between') ctx.warnings.push(...betweenEndWarnings(cond, 'TimeStamp', 'whole days'));
      } else {
        ctx.warnings.push(`whole days on "${cond.field}" needs a Date & time field (found ${t.seen}) — the condition compares exact moments`);
      }
    }
  }
  // Whole days has nothing to compare on an is-null operator or an AAOB label — say so instead of dropping it silently.
  if (cond.wholeDays && (isAaob || cond.op === '_is_null')) {
    ctx.warnings.push(`whole days on "${cond.field}" needs a Date & time field (found ${isAaob ? 'AAOB field' : 'is null operator'}) — the condition compares exact moments`);
  }

  const dynOp = isDynExpr(cond.op);
  const dynConn = isDynExpr(connector);

  const b = mk(ctx, 'add_condition');
  if (!b) return null;
  b.data = JSON.stringify(data);
  // between: the OP dropdown builds its options from block.data and caches them from construction, when data
  // was still empty — so rebuild them now, or the Number / Date & time operator list (with _between) is not
  // the one sf(OP) validates against. Only for between: every other operator keeps validating exactly as before.
  if (!isAaob && cond.op === '_between' && data.dataType) { try { b.getField('OP')?.getOptions(false); } catch { /* not a dropdown */ } }
  sf(b, 'TARGET', on);
  sf(b, 'FIELD', cond.field);
  if (isAaob) sf(b, 'AAOBTYPE', vt);
  sf(b, 'OP', dynOp ? '__dynamic__' : opToFieldImp(cond.op as string, cond.value));
  sf(b, 'CONNECTOR', dynConn ? '__dynamic__' : connector as string);
  // Missing value: every Forge-built condition is false unless Forge asked to skip (zavo_Sep_01).
  sf(b, 'ONMISSING', cond.ifMissing === 'skip' ? 'skip' : 'false');
  if (wholeDays) sf(b, 'WHOLEDAYS', 'TRUE');

  if (isAaob) cv(b, 'KEY', expr(cond.aaobKey!, ctx));
  if (dynOp) {
    cv(b, 'DYNOP', expr(cond.op as Expression, ctx));
    cv(b, 'VALUE', expr(cond.value, ctx));
  } else if (cond.op !== '_is_null') {
    cv(b, 'VALUE', expr(cond.value, ctx));
  }
  // Was (until 2026-10-09): "to" only for a picked between.
  // if (cond.op === '_between' && cond.valueTo !== undefined) cv(b, 'VALUETO', expr(cond.valueTo, ctx));
  if ((cond.op === '_between' || dynOp) && cond.valueTo !== undefined) cv(b, 'VALUETO', expr(cond.valueTo, ctx));
  if (dynConn) cv(b, 'DYNCONN', expr(connector as Expression, ctx));

  return ir(b);
}

function mkAaobAddConditionBlock(
  item: QueryAaobFilter, defaultOn: string, connector: 'AND' | 'OR', ctx: Ctx
): any {
  const vt = AAOB_VALUE_TYPE;   // never item.valueType — see AAOB_VALUE_TYPE
  const data = { onPath: defaultOn, source: 'aaob', aaobField: item.aaobField, dataType: vt };

  const b = mk(ctx, 'add_condition');
  if (!b) return null;
  b.data = JSON.stringify(data);
  sf(b, 'TARGET', defaultOn);
  sf(b, 'FIELD', item.aaobField);
  sf(b, 'AAOBTYPE', vt);
  sf(b, 'OP', item.op);
  sf(b, 'CONNECTOR', connector);
  sf(b, 'ONMISSING', 'false'); // a new block, so never 'legacy' (zavo_Sep_01)
  cv(b, 'KEY', expr(item.key, ctx));
  cv(b, 'VALUE', expr(item.value, ctx));
  return ir(b);
}

function mkAaobLabelExistsCondBlock(
  item: QueryAaobLabelExists, defaultOn: string, connector: 'AND' | 'OR', ctx: Ctx
): any {
  const data = { onPath: defaultOn, source: 'aaob', aaobField: item.aaobField, dataType: AAOB_VALUE_TYPE };

  const b = mk(ctx, 'add_condition');
  if (!b) return null;
  b.data = JSON.stringify(data);
  sf(b, 'TARGET', defaultOn);
  sf(b, 'FIELD', item.aaobField);
  sf(b, 'AAOBTYPE', 'String');
  sf(b, 'OP', '_isnull_false');
  sf(b, 'CONNECTOR', connector);
  sf(b, 'ONMISSING', 'false'); // a new block, so never 'legacy' (zavo_Sep_01)
  cv(b, 'KEY', expr(item.key, ctx));
  return ir(b);
}

function mkConditionGroupBlock(
  connector: 'AND' | 'OR', onPath: string, bodyBlocks: any[], ctx: Ctx
): any {
  const b = mk(ctx, 'condition_group');
  if (!b) return null;
  b.data = JSON.stringify({ onPath });
  sf(b, 'TARGET', onPath);
  sf(b, 'CONNECTOR', connector);
  const bodyHead = chainStatementBlocks(bodyBlocks);
  if (bodyHead) cs(b, 'BODY', bodyHead);
  return ir(b);
}

function emitImperativeOrderBlocks(
  step: { orderBy?: any; aaobOrderBy?: any }, defaultOn: string, ctx: Ctx
): any[] {
  const orders: any[] = [];
  if (step.orderBy) {
    const list = Array.isArray(step.orderBy) ? step.orderBy : [step.orderBy];
    orders.push(...list);
  }
  if (step.aaobOrderBy) {
    const a = step.aaobOrderBy;
    orders.push({ field: a.aaobField, direction: a.direction, aaobKey: a.key, aaobType: AAOB_VALUE_TYPE });
  }
  if (orders.length === 0) return [];
  const targets = new Set<string>();
  for (const o of orders) targets.add(o.on || defaultOn);
  const clears = [...targets].map(t => mkClearBlock('clear_orders', t, ctx)).filter(Boolean);
  const addBlocks = orders.map(o => mkAddOrderBlock(o, defaultOn, ctx)).filter(Boolean);
  return [...clears, ...addBlocks];
}

function mkAddOrderBlock(order: any, defaultOn: string, ctx: Ctx): any {
  const on = order.on || defaultOn;
  const dir = order.direction || 'asc';
  const isAaob = order.aaobKey !== undefined;
  const data: any = { onPath: on, source: isAaob ? 'aaob' : 'field' };
  if (isAaob) { data.aaobField = order.field; data.dataType = AAOB_VALUE_TYPE; }
  else data.path = order.field.split('.').filter(Boolean);

  const b = mk(ctx, 'add_order');
  if (!b) return null;
  b.data = JSON.stringify(data);
  sf(b, 'TARGET', on);
  sf(b, 'FIELD', order.field);
  if (isAaob) sf(b, 'AAOBTYPE', AAOB_VALUE_TYPE);
  sf(b, 'DIR', dir);
  if (isAaob) cv(b, 'KEY', expr(order.aaobKey, ctx));
  return ir(b);
}

// === Entity Reference Chain ===

function buildEntRefChain(refs: { field: string; entityVar: string; mode?: 'link' | 'build'; considerEntity?: boolean }[], ctx: Ctx): any | null {
  let current: any = null;
  for (let i = refs.length - 1; i >= 0; i--) {
    const ref = refs[i];
    const b = mk(ctx, 'entity_reference_selection');
    sf(b, 'VAR', ref.field);
    // mode (link/build) takes precedence; fall back to the legacy considerEntity boolean.
    sf(b, 'CONSIDER_ENTITY', (ref.mode ? ref.mode === 'link' : ref.considerEntity) ? 'TRUE' : 'FALSE');
    const g = mk(ctx, 'get_entity_variable');
    setVar(g, 'VAR', ref.entityVar, ctx);
    cv(b, 'ENTITY', ir(g));
    if (current) cv(b, 'NEXT', current);
    current = ir(b);
  }
  return current;
}

// === Block Helpers ===

function mk(ctx: Ctx, blockType: string, fields?: Record<string, string | number | boolean>): any {
  try {
    const b = ctx.ws.newBlock(blockType);
    if (fields) {
      for (const [k, v] of Object.entries(fields)) {
        try { b.setFieldValue(String(v), k); } catch { /* field may not exist */ }
      }
    }
    return b;
  } catch (e: any) {
    ctx.errors.push(`Unknown block type "${blockType}": ${e.message}`);
    return null;
  }
}

function ir(block: any): any {
  if (block) { block.initSvg(); block.render(); }
  return block;
}

function sf(block: any, fieldName: string, value: string | number | boolean): void {
  if (!block) return;
  try { block.setFieldValue(String(value), fieldName); } catch { /* skip */ }
}

/** Puts a people block's colours back; a value that is not #RRGGBB is refused by the field and the default stays. */
function setPeopleTheme(b: any, accent: unknown, surface: unknown, text: unknown): void {
  const put = (name: string, v: unknown) => {
    if (typeof v === 'string' && v) { try { b.setFieldValue(v, name); } catch { /* refused: the default stays */ } }
  };
  put('ACCENT', accent); put('SURFACE', surface); put('TEXT', text);
}

function peopleBlockFromStep(type: string, a: Expression[], ctx: Ctx): any {
  const b = mk(ctx, type);
  if (!b) return null;
  cv(b, 'PERSON', expr(a[0], ctx));
  setPeopleTheme(b, a[1], a[2], a[3]);
  return ir(b);
}

function setVar(block: any, fieldName: string, encodedId: string, ctx: Ctx): void {
  if (!block || !encodedId) return;
  // System-word variables (name contains "system") are READ-ONLY on MOST set_* blocks: refuse them —
  // warn + leave the field unset (NAN → a visible no-op, not a silent write). EXCEPT the entity setters
  // in SYSTEM_SET_VISIBLE_BLOCKS (set_entity_variable / set_entity_list_variable / set_aob_variable, + the
  // reference-setter): those write INTO an entity and system-word fields are stripped from the create/update
  // payload, so a set there is a harmless no-op — allowed (2026-09-03). set_variable / the legacy typed
  // setters still refuse. See connection-rulebook / aaob-model system-fields rule + project_system_fields_rule.
  if (fieldName === 'VAR' && typeof block.type === 'string' && block.type.startsWith('set_') && !SYSTEM_SET_VISIBLE_BLOCKS.has(block.type)) {
    const leaf = String(encodedId).split(/[$%]/).pop() || '';
    if (leaf.toLowerCase().includes('system')) {
      ctx.warnings.push(
        `Refused to SET system-word variable "${encodedId}" on ${block.type} — system variables are ` +
        `read-only (use a get_* block to read them).`
      );
      return; // leave VAR unset
    }
  }
  // AAOB rule: a general AOB block must not target an AAOB reference — only the dedicated AAOB blocks may.
  if (fieldName === 'VAR' && isGeneralAobBlockTargetingAaob(block.type, encodedId, ctx.pc?.entityListReferences)) {
    ctx.warnings.push(
      `Refused AAOB target "${encodedId}" on ${block.type} — AAOB fields may be touched only via ` +
      `set_key_value_in_AOB_reference / aaob_aggregate_by_label. Left the field unset.`
    );
    return; // leave VAR unset
  }
  ensureVar(encodedId, ctx);
  const blockField = block.getField(fieldName);
  if (blockField?.referencesVariables?.()) {
    const v = ctx.ws.getVariableById(encodedId) || ctx.ws.getVariable(encodedId);
    if (v) {
      try { block.setFieldValue(v.getId(), fieldName); } catch { /* skip */ }
      // A variable field REFUSES a variable of a type it does not accept, and it does so without throwing:
      // it logs to the console and keeps its default (`NAN`), and the block then generates no code. That is
      // how a record's list reached a delete block typed '' and the step vanished from the function, with
      // no warning here (2026-09-28, .scratch/forge-nested-list-delete). Say so.
      if (block.getFieldValue(fieldName) !== v.getId()) {
        ctx.warnings.push(
          `Variable "${encodedId}" (type ${v.type || 'unknown'}) was refused by ${block.type}.${fieldName} — ` +
          `the slot is empty and this step will do nothing.`
        );
      }
    } else {
      ctx.warnings.push(`Variable "${encodedId}" not found for ${block.type}.${fieldName}`);
    }
  } else {
    sf(block, fieldName, encodedId);
  }
}

function ensureVar(encodedId: string, ctx: Ctx): void {
  if (!encodedId || ctx.ws.getVariableById(encodedId)) return;
  const vt = resolveVarType(encodedId, ctx);
  ctx.ws.createVariable(encodedId, vt, encodedId);
}

function cv(parent: any, inputName: string, child: any): void {
  if (!parent || !child) return;
  const input = parent.getInput(inputName);
  if (input?.connection && child.outputConnection) {
    try { input.connection.connect(child.outputConnection); return; } catch { /* type mismatch — retry below */ }
    // TYPE COERCION (2026-08-27). The first connect can fail when the child's output type (e.g.
    // TimeStamp from a variable getter) doesn't match the input's check (e.g. ["String","Array"] on
    // text_isEmpty). Widening the child's output to null (wildcard) lets the connection succeed.
    // Safe: this workspace is headless and temporary (code generation only), and the project's own
    // get_variable block already uses the same null-output trick during deserialization.
    // Before this fix, the child was silently disposed and the input used its empty default — e.g.
    // text_isEmpty compiled to `!''.length` (always true) instead of `!variable.length`.
    try { child.outputConnection.setCheck(null); input.connection.connect(child.outputConnection); return; } catch { /* genuinely incompatible */ }
  }
  // ORPHAN DISPOSAL (2026-08-05). Skipping the connection is not enough: `child` was ALREADY built and
  // rendered before cv was called — expr() ends in ir(), which runs initSvg() + render(), and for a null
  // argument expr() returns a real `logic_null` BLOCK, not null. Dropping the reference here leaves it
  // floating on the canvas, and the next save serializes it into the function's XML.
  // This bites hardest on shape-changing blocks: text_getSubstring with WHERE2=LAST has no AT2 input
  // (updateAt_ appends a dummy with a null connection), so `cv(b,'AT2', expr(a[2],ctx))` evaluates a
  // logic_null that can never attach. text_charAt has the identical shape. Pre-existing, but the
  // WHERE1/WHERE2 fix makes the AT inputs genuinely disappear, so it is now reachable by design.
  // The invariant this restores: cv either connects the child or destroys it — never leaves it adrift.
  try { child.dispose(false); } catch { /* already gone */ }
}

// NOT given cv's orphan disposal, deliberately. `child` here is the head of a STATEMENT CHAIN — a run of
// real user steps. Disposing it on a failed connect would delete work rather than tidy a stray literal,
// and unlike cv there is no shape-changing block that makes the failure expected. A leaked statement
// chain stays visible on the canvas, which is the safer failure. Revisit only with a case that shows it.
function cs(parent: any, inputName: string, child: any): void {
  if (!parent || !child) return;
  const input = parent.getInput(inputName);
  if (input?.connection && child.previousConnection) {
    try { input.connection.connect(child.previousConnection); } catch { /* skip */ }
  }
}

// === Type Resolution ===

function resolveVarType(encodedId: string, ctx: Ctx): string {
  for (const v of ctx.pc.localVariables) {
    if (v.encodedId === encodedId) return v.type;
    if (v.entityFields) {
      for (const f of v.entityFields) { if (f.encodedId === encodedId) return f.type; }
    }
  }
  for (const v of ctx.pc.globalVariables) {
    if (v.encodedId === encodedId) return v.type;
    if (v.entityFields) {
      for (const f of v.entityFields) { if (f.encodedId === encodedId) return f.type; }
    }
  }
  for (const e of ctx.pc.entities) {
    for (const f of e.fields) { if (f.encodedId === encodedId) return f.type; }
  }
  for (const e of ctx.pc.entityListReferences) {
    for (const f of e.fields) { if (f.encodedId === encodedId) return f.type; }
  }
  if (encodedId.includes('ENTITYNAME') || ctx.pc.entities.some(e => e.id === encodedId)) return 'ENTITYNAME';
  return 'String';
}

function findEntity(name: string, ctx: Ctx): PageContextEntity | null {
  const lower = name.toLowerCase();
  return ctx.pc.entities.find(e => e.name.toLowerCase() === lower || e.id.toLowerCase() === lower) || null;
}

/**
 * between (fix round 2, 2026-10-07): once a between block is stamped with its field's type, its "from" / "to"
 * sockets take that type's values only (VALUE_CHECK_FOR_TYPE) — and Blockly unplugs a child that fails a
 * check, so a plain text on a Number or Date & time field, or a plain number on a Date & time field, would
 * silently vanish and the condition would generate `null` ends. A hand-built block refuses the same literals,
 * so the socket stays strict; this names each refused end, read from the step's Expression (never from the
 * built block), so Forge and the builder can fix it. A fn or a var is left alone: whether it fits is the
 * socket's business.
 *
 * `what` (2026-10-09) names the condition the warning is about: 'between' (the default — every existing caller
 * and text is unchanged) checks both ends; 'whole days' checks the single `value` only, since a non-between
 * operator has no "to", and words the warning about that value.
 */
// Was (until 2026-10-09): function betweenEndWarnings(cond: QueryCondition, type: 'Number' | 'TimeStamp'): string[] {
// (it always spoke of "between" and the "from" / "to" ends — wrong for a whole-days _lte / _eq, which has one value)
function betweenEndWarnings(cond: QueryCondition, type: 'Number' | 'TimeStamp', what: 'between' | 'whole days' = 'between'): string[] {
  const out: string[] = [];
  // Was (until 2026-10-09): const ends: ['from' | 'to', Expression | undefined][] = [['from', cond.value], ['to', cond.valueTo]];
  const ends: ['from' | 'to' | 'value', Expression | undefined][] = what === 'whole days'
    ? [['value', cond.value]]
    : [['from', cond.value], ['to', cond.valueTo]];
  for (const [which, e] of ends) {
    const kind = typeof e === 'string' ? 'text' : (typeof e === 'number' && type === 'TimeStamp') ? 'number' : '';
    if (!kind) continue;
    const label = type === 'TimeStamp' ? 'Date & time' : 'Number';
    const fix = type === 'TimeStamp'
      ? 'use a date block (e.g. start of day / end of day) or a Date & time variable'
      : 'use a number block or a Number variable';
    // Was (until 2026-10-09): out.push(`between on "${cond.field}": the ${which} value is a plain ${kind}, which a ${label} field won't accept — ${fix}`);
    out.push(`${what} on "${cond.field}": the ${which} ${which === 'value' ? 'is' : 'value is'} a plain ${kind}, which a ${label} field won't accept — ${fix}`);
  }
  return out;
}

/**
 * between (2026-10-07): the Number / Date & time type of the field a between condition filters on, or null
 * when it is neither — with what was actually found, for the warning. `on` is a dot-path ("Student" or
 * "Student.subjects"), so the entity is looked up by the whole path, then by its last segment, in the page's
 * entities and its entity-list references. A drilled `field` ("classRef.name") is not resolved: it reads
 * as "nothing" and the caller warns.
 */
function betweenFieldType(
  cond: QueryCondition, defaultOn: string, ctx: Ctx
): { type: 'Number' | 'TimeStamp' | null; seen: string } {
  const on = cond.on || defaultOn;
  const last = on.split('.').pop() || on;
  const pool = [...ctx.pc.entities, ...(ctx.pc.entityListReferences || [])];
  const same = (e: PageContextEntity, n: string) => e.name.toLowerCase() === n.toLowerCase() || e.id.toLowerCase() === n.toLowerCase();
  const entity = pool.find(e => same(e, on)) || pool.find(e => same(e, last)) || null;
  const field = resolveEntityField(entity, cond.field);
  const prim = field ? getFieldPrimType(field.type) : '';
  return { type: prim === 'Number' || prim === 'TimeStamp' ? prim : null, seen: field ? (field.type || 'nothing') : 'nothing' };
}

function resolveEntityField(entity: PageContextEntity | null, fieldName: string): PageContextEntityField | null {
  if (!entity) return null;
  const lower = fieldName.toLowerCase();
  return entity.fields.find(f => f.name.toLowerCase() === lower) || null;
}

function getFieldPrimType(type: string): string {
  switch (type) {
    case 'String': return 'String';
    case 'Number': return 'Number';
    case 'Boolean': return 'Boolean';
    case 'TimeStamp': return 'TimeStamp';
    default: return 'String';
  }
}

// === Type Guards ===

function isVarRef(e: Expression): e is ExpressionVar {
  return typeof e === 'object' && e !== null && 'var' in e;
}
function isBinaryOp(e: Expression): e is ExpressionBinaryOp {
  return typeof e === 'object' && e !== null && 'op' in e && 'left' in e;
}
function isUnaryOp(e: Expression): e is ExpressionUnaryOp {
  return typeof e === 'object' && e !== null && 'op' in e && 'arg' in e;
}
function isFnCall(e: Expression): e is ExpressionFnCall {
  return typeof e === 'object' && e !== null && 'fn' in e;
}
function isOrCondition(item: QueryConditionItem): item is QueryOrCondition {
  return 'or' in item;
}
function isAndCondition(item: QueryConditionItem): item is QueryAndCondition {
  return 'and' in item;
}
function isAaobFilter(item: QueryConditionItem): item is QueryAaobFilter {
  return 'aaobField' in item && 'op' in item && 'value' in item;
}
function isAaobLabelExists(item: QueryConditionItem): item is QueryAaobLabelExists {
  return 'aaobField' in item && !('op' in item);
}

// === Path → EncodedId Resolution (dotted-path refactor 2026-07-02) ===

function buildPathMap(ctx: PageContext): Map<string, string> {
  const map = new Map<string, string>();
  for (const v of [...ctx.localVariables, ...ctx.globalVariables]) {
    if (v.path) map.set(v.path, v.encodedId);
    if (v.entityFields) {
      for (const ef of v.entityFields) {
        if (ef.path) map.set(ef.path, ef.encodedId);
      }
    }
  }
  for (const e of ctx.entities) {
    for (const f of e.fields) { if (f.path) map.set(f.path, f.encodedId); }
  }
  for (const e of ctx.entityListReferences) {
    if ((e as any).aobPath) map.set((e as any).aobPath, e.id);
    for (const f of e.fields) { if (f.path) map.set(f.path, f.encodedId); }
  }
  return map;
}

function resolveStepPaths(steps: ForgeStep[], pathMap: Map<string, string>): ForgeStep[] {
  const resolved = JSON.parse(JSON.stringify(steps));
  walkResolve(resolved, pathMap);
  return resolved;
}

function walkResolve(steps: any[], pm: Map<string, string>): void {
  for (const s of steps) {
    if (!s || typeof s !== 'object') continue;
    for (const f of ['target', 'destination', 'indexVar', 'listVar', 'entityVar', 'itemVar', 'parentRef']) {
      if (typeof s[f] === 'string') s[f] = pm.get(s[f]) || s[f];
    }
    for (const f of ['value', 'text', 'url', 'message', 'from', 'to', 'step', 'list', 'times', 'condition', 'limit', 'offset', 'index', 'key']) {
      if (s[f] !== undefined) s[f] = resolveExpr(s[f], pm);
    }
    if (s.fields && typeof s.fields === 'object' && !Array.isArray(s.fields)) {
      for (const k of Object.keys(s.fields)) s.fields[k] = resolveExpr(s.fields[k], pm);
    }
    if (s.entityRefFields && typeof s.entityRefFields === 'object') {
      for (const k of Object.keys(s.entityRefFields)) s.entityRefFields[k] = resolveExpr(s.entityRefFields[k], pm);
    }
    if (s.entityReferences && Array.isArray(s.entityReferences)) {
      for (const ref of s.entityReferences) { if (typeof ref.entityVar === 'string') ref.entityVar = pm.get(ref.entityVar) || ref.entityVar; }
    }
    if (typeof s.successFlag === 'string') s.successFlag = pm.get(s.successFlag) || s.successFlag;
    if (s.inputs && typeof s.inputs === 'object') {
      for (const k of Object.keys(s.inputs)) s.inputs[k] = resolveExpr(s.inputs[k], pm);
    }
    if (s.outputs && typeof s.outputs === 'object') {
      for (const k of Object.keys(s.outputs)) { if (typeof s.outputs[k] === 'string') s.outputs[k] = pm.get(s.outputs[k]) || s.outputs[k]; }
    }
    if (Array.isArray(s.branches)) {
      for (const b of s.branches) { if (b?.condition) b.condition = resolveExpr(b.condition, pm); if (Array.isArray(b?.then)) walkResolve(b.then, pm); }
    }
    for (const f of ['else', 'body', 'try', 'catch', 'finally']) { if (Array.isArray(s[f])) walkResolve(s[f], pm); }
  }
}

function resolveExpr(expr: any, pm: Map<string, string>): any {
  if (expr === null || expr === undefined || typeof expr === 'string' || typeof expr === 'number' || typeof expr === 'boolean') return expr;
  if (Array.isArray(expr)) return expr.map(e => resolveExpr(e, pm));
  if (typeof expr !== 'object') return expr;
  if (expr.var !== undefined && typeof expr.var === 'string') expr.var = pm.get(expr.var) || expr.var;
  if (expr.left !== undefined) expr.left = resolveExpr(expr.left, pm);
  if (expr.right !== undefined) expr.right = resolveExpr(expr.right, pm);
  if (expr.arg !== undefined) expr.arg = resolveExpr(expr.arg, pm);
  if (expr.args && Array.isArray(expr.args)) expr.args = expr.args.map((a: any) => resolveExpr(a, pm));
  return expr;
}
